import { apiConfig } from "../configuration";
import router from "../router";
import { store } from "../store";

const baseUrl = apiConfig.baseUrl;

type Params = { [key: string]: string };

const urlBuilder = (action: string, params?: Params) => {
    const mainUrl = baseUrl + action;
    if (params) {
        const flatParams = Object.keys(params).map(k => ({ k, v: params[k] }));
        return mainUrl + "?" + flatParams.map(p => `${encodeURI(p.k)}=${encodeURI(p.v)}`).join("&");
    }
    return mainUrl;
}

type WhoAmIResponse = {
    user: string;
};

type AddExpenseRequest = {
    amount: number,
    description: string,
    date: Date,
    tags: string[]
    sensitive: boolean;
};

export type BudgetBucket = {
    id: number;
    name: string;
    slug: string;
    isActive: boolean;
}

export type BudgetAllocation = {
    id: number;
    budgetBucketId: number;
    year: number;
    annualLimitCents: number | null;
    monthlyLimitCents: number | null;
}

export type ExpectedIncome = {
    id: number;
    year: number;
    expectedIncomeCents: number | null;
}

export type SavingsForecast = {
    year: number;
    expectedIncomeCents: number | null;
    savingsTargetCents: number;
    rows: SavingsForecastRow[];
}

export type SavingsForecastRow = {
    month: string;
    projectionRatio: number;
    actualSavingsCents: number;
    projectedSpendCents: number;
    projectedSavingsCents: number;
    savingsTargetCents: number;
}

export type BudgetBucketSummary = {
    id: number;
    name: string;
    slug: string;
}

export type BudgetBucketRule = {
    id: number;
    budgetBucketId: number;
    priority: number;
    description: string;
    isActive: boolean;
    matchers: ExpressionMatcher[][];
}

export type SpendingDecisionState = {
    summary: string;
    draftNotes: string;
    updatedAt: string;
}

export type PurchaseAdviceRequest = { description: string; amountCents: number | null; merchant: string; notes: string; budgetBucketId: number | null; includeSensitive: boolean; }
export type SuggestedMemoryChange = { action: "add" | "update"; existingMemoryId: number; key: string; title: string; body: string; severity: "info" | "caution" | "hard-rule"; scope: "global" | "bucket" | "merchant" | "tag" | "purchase-type"; budgetBucketId: number; merchantPattern: string; tags: string[]; }
export type PurchaseAdviceResult = { verdict: "buy" | "wait" | "skip" | "needs-more-info"; confidence: "low" | "medium" | "high"; rationale: string; budgetImpact: string; cheaperAlternative: string; suggestedMemoryUpdates: SuggestedMemoryChange[]; }
export type PurchaseAdviceResponse = { advice: PurchaseAdviceResult; context: { bucket: BudgetBucketSummary | null; relevantMemories: Array<{ id: number; title: string; body: string; severity: string }>; similarExpenses: Array<{ date: string; amountCents: number; description: string }> }; costWarning: string | null; estimatedCostCents: number; }
export type AgentSettings = { model: string; maxOutputTokens: number; monthlyHardCapCents: number; dailySoftCapCents: number; dailyHardCapCents: number; perRequestSoftCapCents: number; perRequestHardCapCents: number; }
export type AgentUsageGroup = { name: string; calls: number; costCents: number; }
export type AgentUsageRow = { timestamp: string; provider: string; model: string; feature: string; inputTokens: number; outputTokens: number; estimatedCostCents: number; status: string; errorMessage: string | null; }
export type AgentAdminView = { settings: AgentSettings; monthCostCents: number; dayCostCents: number; callsThisMonth: number; callsByFeature: AgentUsageGroup[]; callsByModel: AgentUsageGroup[]; recentFailures: AgentUsageRow[]; expensiveCalls: AgentUsageRow[]; }

export type OutputExpense = AddExpenseRequest & {
    id: number;
    hasDetails?: boolean;
    isSplitChild?: boolean;
    splitId?: number | null;
    parentExpenseId?: number | null;
    budgetBucket?: BudgetBucketSummary | null;
    ruleBudgetBucket?: BudgetBucketSummary | null;
    budgetBucketOverride?: BudgetBucketSummary | null;
    setBudgetBucketOverride?: boolean;
    budgetBucketOverrideId?: number | null;
};

type ListExpenseResponse = {
    expenses: OutputExpense[]
};

export type Mapping = {
    source: string;
    expressions: MappingExpression[]
}

export type MappingExpression = {
    expression: string;
    template: string;
}

export type ExpressionMatcher = {
    type: "Description" | "Tag" | "Amount";
    expression: string;
}

export type AutoTagRule = {
    id?: number;
    tag: string;
    description: string;
    matchers: ExpressionMatcher[][];
}

export type DataQualityReport = {
    generatedAt: string;
    autoTagRuleCount: number;
    mappingCount: number;
    periods: DataQualityPeriod[];
}

export type DataQualityPeriod = {
    label: string;
    from: string | null;
    to: string | null;
    rowCount: number;
    positiveRowCount: number;
    incomeRowCount: number;
    positiveSpend: number;
    excludedSpend: number;
    excludedRowCount: number;
    reportableSpend: number;
    income: number;
    untaggedSpend: number;
    untaggedRowCount: number;
    untaggedShare: number;
    sourceCounts: NamedCount[];
    tagDistribution: CountBucket[];
    topTags: TagSummary[];
    topCategoryTags: TagSummary[];
    topUntaggedClusters: UntaggedCluster[];
    duplicateCandidates: DuplicateCandidate[];
    excludedExpenses: ExpenseSummary[];
}

export type NamedCount = {
    name: string;
    count: number;
}

export type CountBucket = {
    bucket: number;
    count: number;
}

export type TagSummary = {
    tag: string;
    count: number;
    amount: number;
}

export type UntaggedCluster = {
    description: string;
    count: number;
    amount: number;
    example: ExpenseSummary;
}

export type DuplicateCandidate = {
    date: string;
    amount: number;
    description: string;
    count: number;
    exampleIds: number[];
}

export type ExpenseSummary = {
    id: number;
    date: string;
    amount: number;
    description: string;
    source: string;
    tags: string[];
}

export type InsightsReport = {
    generatedAt: string;
    periods: InsightsPeriod[];
}

export type InsightsPeriod = {
    label: string;
    from: string | null;
    to: string | null;
    total: number;
    monthlyTotals: MonthlySpend[];
    categorySpend: TagSpend[];
    flexibleSpend: TagSpend[];
    shopping: ShoppingInsight;
    untagged: SpendSummary;
}

export type MonthlySpend = {
    month: string;
    amount: number;
    count: number;
}

export type TagSpend = {
    tag: string;
    amount: number;
    refundAmount: number;
    netAmount: number;
    count: number;
}

export type ShoppingInsight = {
    total: number;
    count: number;
    amazonItems: AmazonShoppingInsight;
    largePurchaseThreshold: number;
    buckets: SpendBucket[];
    largePurchases: InsightExpenseSummary[];
}

export type AmazonShoppingInsight = {
    total: number;
    count: number;
    categories: TagSpend[];
    topItems: AmazonItemSummary[];
    reviewItems: AmazonItemSummary[];
}

export type AmazonItemSummary = {
    orderId: string;
    date: string;
    title: string;
    merchant: string | null;
    category: string;
    amount: number;
}

export type SpendBucket = {
    label: string;
    amount: number;
    count: number;
}

export type SpendSummary = {
    amount: number;
    count: number;
}

export type InsightExpenseSummary = {
    id: number;
    date: string;
    amount: number;
    description: string;
    tags: string[];
}

export type AmazonImportPreview = {
    fileName: string;
    orderCount: number;
    matchedCount: number;
    needsReviewCount: number;
    alreadyImportedCount: number;
    orders: AmazonOrderPreview[];
}

export type AmazonOrderPreview = {
    order: AmazonExtensionOrder;
    date: string | null;
    alreadyImported: boolean;
    matchStatus: "matched" | "needs-review" | "unmatched" | "no-bank-charge";
    matchedChargeExpenseId: number | null;
    matchedRefundExpenseId: number | null;
    chargeCandidates: AmazonExpenseCandidate[];
    refundCandidates: AmazonExpenseCandidate[];
    chargeMatches: AmazonExpenseMatch[];
    refundMatches: AmazonExpenseMatch[];
}

export type AmazonExtensionOrder = {
    orderId: string;
    rawDate: string;
    totalCents: number | null;
    refundCents: number | null;
    isRefunded: boolean;
    discountCents: number | null;
    giftCardCents: number | null;
    itemTotalCents: number | null;
    reconciles: boolean | null;
    currency: string;
    paymentMethod: string;
    pageUrl: string;
    items: AmazonExtensionItem[];
}

export type AmazonExtensionItem = {
    title: string;
    asin: string | null;
    productUrl: string | null;
    imageUrl: string | null;
    merchant: string | null;
    quantity: number | null;
    unitPriceCents: number | null;
    amountCents: number | null;
}

export type AmazonExpenseCandidate = {
    id: number;
    date: string;
    amount: number;
    description: string;
    source: string;
    reference: string | null;
}

export type AmazonExpenseMatch = {
    expense: AmazonExpenseCandidate;
    kind: "charge" | "refund";
    matchType: "exact" | "split-charge" | "split-refund" | "grouped-orders" | "grouped-refunds";
    amountCents: number;
}

export type AmazonImportResult = {
    id: number;
    orderCount: number;
    matchedCount: number;
    needsReviewCount: number;
    skippedAlreadyImported: number;
}

export type AmazonRematchResult = {
    orderCount: number;
    previousMatchedCount: number;
    previousNeedsReviewCount: number;
    previousUnmatchedCount: number;
    matchedCount: number;
    needsReviewCount: number;
    unmatchedCount: number;
    noBankChargeCount: number;
}

export type AmazonOrderListResponse = {
    orders: AmazonOrderListItem[];
}

export type AmazonOrderListItem = {
    id: number;
    orderId: string;
    rawDate: string;
    date: string | null;
    totalCents: number | null;
    refundCents: number | null;
    isRefunded: boolean;
    discountCents: number | null;
    giftCardCents: number | null;
    itemTotalCents: number | null;
    reconciles: boolean | null;
    paymentMethod: string | null;
    pageUrl: string | null;
    matchStatus: "matched" | "needs-review" | "unmatched" | "no-bank-charge";
    items: AmazonExtensionItem[];
    matches: AmazonOrderExpenseListMatch[];
}

export type AmazonOrderExpenseListMatch = {
    kind: "charge" | "refund";
    matchType: "exact" | "split-charge" | "split-refund" | "grouped-orders" | "grouped-refunds";
    amountCents: number;
    expense: AmazonExpenseCandidate | null;
}

export type ExpenseDetails = {
    type: "amazon" | "split";
    expenseId: number;
    split?: {
        id: number;
        parentExpenseId: number;
        sourceType: string;
        sourceId: string | null;
        isManaged: boolean;
        hasManualEdits: boolean;
        parent: {
            id: number;
            date: string;
            amount: number;
            description: string;
            source: string;
        };
        children: Array<{
            id: number;
            amount: number;
            description: string;
            splitSourceItemId: string | null;
            tags: string[];
        }>;
    } | null;
    matches: AmazonExpenseDetailMatch[];
}

export type ApiKeySummary = {
    id: number;
    name: string;
    keyPrefix: string;
    scopes: string;
    createdAt: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
    secret?: string | null;
}

export type ApiKeyListResponse = {
    keys: ApiKeySummary[];
}

export type PendingAction = {
    token: string;
    kind: string;
    summary: string;
    payload: unknown;
    createdAt: string;
    expiresAt: string;
    executedAt: string | null;
    cancelledAt: string | null;
    lastFailedAt: string | null;
    lastErrorMessage: string | null;
    isExpired: boolean;
    isTerminal: boolean;
}

export type PendingActionResult = {
    expenseIds?: number[];
    isOk: boolean;
    message: string;
}

export type AmazonExpenseDetailMatch = {
    kind: "charge" | "refund";
    matchType: "exact" | "split-charge" | "split-refund" | "grouped-orders" | "grouped-refunds";
    amountCents: number;
    order: AmazonExpenseDetailOrder;
}

export type AmazonExpenseDetailOrder = {
    orderId: string;
    rawDate: string;
    date: string | null;
    totalCents: number | null;
    refundCents: number | null;
    discountCents: number | null;
    giftCardCents: number | null;
    paymentMethod: string | null;
    items: AmazonExpenseDetailItem[];
}

export type AmazonExpenseDetailItem = {
    title: string;
    merchant: string | null;
    quantity: number | null;
    unitPriceCents: number | null;
    amountCents: number | null;
}

type MapItemsResponse = {
    mapItems: Mapping[];
}


const isFormData = (e: unknown): e is FormData => {
    return e instanceof FormData;
}

export const withLoading = async (inner: () => Promise<void>): Promise<void> => {
    store.loading++;
    try {
        await inner();
    } finally {
        store.loading--;
    }
}

const fetchWrapper = async (action: string, type: "GET" | "POST" | "PUT" | "DELETE", data?: {
    body?: unknown,
    params?: Params
}): Promise<Response> => {
    store.loading++;
    let hasRedirected = false;
    try {
        const params = data && data.params;
        const body = data && data.body ? (((typeof data.body === "string") || isFormData(data.body)) ? data.body : JSON.stringify(data.body)) : undefined;
        const headers: HeadersInit = {
            "X-Requested-With": "XMLHttpRequest",
        };
        if (!isFormData(data?.body)) {
            headers["content-type"] = "application/json";
        }
        const result = await fetch(urlBuilder(action, params), {
            method: type,
            body: body,
            credentials: 'include',
            headers
        });
        if (!result.ok && result.status === 401) {
            store.user = undefined;
            hasRedirected = true;
            router.push({ name: "login" });
        }
        return result;
    }
    catch (e) {
        if (!hasRedirected && router.currentRoute.name !== "login") {
            console.log(router.currentRoute);
            router.push({ name: "login" });
        }
        throw e;
    } finally {
        store.loading--;
    }
};

export type ImportType = "Poste" | "Paypal" | "Edenred";

let cachedTags: Set<string> | null = null;

const updateTags = (newTags: string[]) => {
    if (cachedTags === null) {
        cachedTags = new Set<string>(newTags);
    }
    for (const tag of newTags) {
        const normalized = tag.toLowerCase();
        if (!cachedTags.has(normalized)) {
            cachedTags.add(normalized);
        }
    }
}

export const api = {
    async createUser(name: string, password: string): Promise<{id: number} | null> {
        const rawResponse = await fetchWrapper("user", "POST", {
            body: {
                username: name,
                password: password
            }
        });
        if (!rawResponse.ok) {
            return null;
        }
        const res = await rawResponse.json() as {id: number};
        return res;
        
    },
    async changePassword(current: string, $new: string): Promise<{ isOk: true } | { isOk: false, reason: string }> {
        const rawResponse = await fetchWrapper("auth/password", "PUT", {
            body: {
                currentPassword: current,
                newPassword: $new
            }
        });
        if (!rawResponse.ok) {
            try {
                const reason = await rawResponse.json() as { message?: string };
                if (!reason || !reason.message) {
                    return { isOk: false, reason: "error" };
                }
                return { isOk: false, reason: reason.message };
            } catch {
                return { isOk: false, reason: "error" };
            }
        }
        return { isOk: true };
    },
    async getTags(): Promise<Set<string>> {
        if (cachedTags !== null) {
            return cachedTags;
        }
        const rawResponse = await fetchWrapper("tag", "GET");
        if (!rawResponse.ok) {
            return new Set<string>();
        }
        const parsed = (await rawResponse.json()) as string[];
        cachedTags = new Set<string>(parsed);
        return cachedTags;
    },
    async getAutoTagRules(): Promise<AutoTagRule[]> {
        const rawResponse = await fetchWrapper("tag/auto", "GET");
        if (!rawResponse.ok) {
            console.error(rawResponse);
            return [];
        }
        const parsed = await rawResponse.json() as AutoTagRule[];
        return parsed;
    },
    async setAutoTagRules(rules: AutoTagRule[]): Promise<void> {
        await fetchWrapper("tag/auto", "PUT", {
            body: rules
        });
    },
    async getBudgetBuckets(): Promise<BudgetBucket[]> {
        const rawResponse = await fetchWrapper("budget/buckets", "GET");
        if (!rawResponse.ok) {
            return [];
        }
        return await rawResponse.json() as BudgetBucket[];
    },
    async setBudgetBuckets(buckets: BudgetBucket[]): Promise<void> {
        await fetchWrapper("budget/buckets", "PUT", {
            body: buckets
        });
    },
    async getBudgetAllocations(year?: number): Promise<BudgetAllocation[]> {
        const rawResponse = await fetchWrapper("budget/allocations", "GET", year ? { params: { year: year.toString() } } : undefined);
        if (!rawResponse.ok) {
            return [];
        }
        return await rawResponse.json() as BudgetAllocation[];
    },
    async setBudgetAllocations(allocations: BudgetAllocation[], year?: number): Promise<void> {
        await fetchWrapper("budget/allocations", "PUT", {
            body: allocations,
            params: year ? { year: year.toString() } : undefined
        });
    },
    async getExpectedIncome(year: number): Promise<ExpectedIncome | null> {
        const rawResponse = await fetchWrapper("budget/expected-income", "GET", {
            params: { year: year.toString() }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as ExpectedIncome;
    },
    async getSavingsForecast(year: number, savingsTargetCents = 500000): Promise<SavingsForecast | null> {
        const rawResponse = await fetchWrapper("budget/savings-forecast", "GET", {
            params: {
                year: year.toString(),
                savingsTargetCents: savingsTargetCents.toString()
            }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as SavingsForecast;
    },
    async setExpectedIncome(expectedIncome: { year: number; expectedIncomeCents: number | null }): Promise<ExpectedIncome | null> {
        const rawResponse = await fetchWrapper("budget/expected-income", "PUT", {
            body: {
                id: 0,
                ...expectedIncome
            }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as ExpectedIncome;
    },
    async getBudgetBucketRules(): Promise<BudgetBucketRule[]> {
        const rawResponse = await fetchWrapper("budget/rules", "GET");
        if (!rawResponse.ok) {
            return [];
        }
        return await rawResponse.json() as BudgetBucketRule[];
    },
    async setBudgetBucketRules(rules: BudgetBucketRule[]): Promise<void> {
        await fetchWrapper("budget/rules", "PUT", {
            body: rules
        });
    },
    async applyBudgetRules(expenses: number[]): Promise<void> {
        await fetchWrapper("budget/apply", "POST", {
            body: expenses
        });
    },
    async getSpendingDecisions(): Promise<SpendingDecisionState | null> {
        const rawResponse = await fetchWrapper("spending-decisions", "GET");
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as SpendingDecisionState;
    },
    async saveSpendingDecisionDraft(draftNotes: string): Promise<SpendingDecisionState | null> {
        const rawResponse = await fetchWrapper("spending-decisions/draft", "PUT", {
            body: { draftNotes }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as SpendingDecisionState;
    },
    async persistSpendingDecisionSummary(summary: string): Promise<SpendingDecisionState | null> {
        const rawResponse = await fetchWrapper("spending-decisions/summary", "POST", {
            body: { summary }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as SpendingDecisionState;
    },
    async getPurchaseAdvice(request: PurchaseAdviceRequest): Promise<PurchaseAdviceResponse> {
        const response = await fetchWrapper("agent/purchase-advice", "POST", { body: request });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: "Unable to get purchase advice." })) as { message?: string };
            throw new Error(error.message || "Unable to get purchase advice.");
        }
        return await response.json() as PurchaseAdviceResponse;
    },
    async getAgentAdmin(): Promise<AgentAdminView | null> {
        const response = await fetchWrapper("agent/admin", "GET");
        return response.ok ? await response.json() as AgentAdminView : null;
    },
    async updateAgentSettings(settings: AgentSettings): Promise<AgentSettings | null> {
        const response = await fetchWrapper("agent/admin/settings", "PUT", { body: settings });
        return response.ok ? await response.json() as AgentSettings : null;
    },
    async applyAgentMemoryChanges(changes: SuggestedMemoryChange[]): Promise<boolean> {
        const response = await fetchWrapper("agent/memory-changes", "POST", { body: changes });
        return response.ok;
    },
    async autoTagAll(expenses: number[]): Promise<void> {
        await fetchWrapper("expense/autotag", "POST", {
            body: expenses 
        });
    },
    async autoMapAll(expenses: number[]): Promise<void> {
        await fetchWrapper("expense/automap", "POST", {
            body: expenses 
        });
    },
    async suggestTags(expense: AddExpenseRequest): Promise<string[]> {
        const rawResponse = await fetchWrapper("tag/expense", "POST", {
            body: {
                ...expense,
                amount: Math.floor(expense.amount * 100)
            }
        });
        if (!rawResponse.ok) {
            console.error(rawResponse);
            return [];
        }
        const parsed = await rawResponse.json() as string[];
        return parsed;
    },
    async logout(): Promise<void> {
        await fetchWrapper("auth/logout", "POST");
    },
    async whoAmI(): Promise<string | null> {
        const rawResponse = await fetchWrapper("auth/whoami", "GET");
        if (!rawResponse.ok) {
            store.user = undefined;
            return null;
        }
        const response = await rawResponse.json() as WhoAmIResponse;
        store.user = response.user;
        return response.user;
    },
    async login(user: string, password: string): Promise<string | null> {
        const rawResponse = await fetchWrapper("auth/login", "POST", {
            body: {
                user,
                password
            }
        });
        if (!rawResponse.ok) {
            return null;
        }
        return user;
    },
    async addExpense(request: AddExpenseRequest): Promise<boolean> {
        const rawResponse = await fetchWrapper("expense", "POST", {
            body: {
                ...request,
                amount: Math.floor(request.amount * 100)
            }
        });
        const isOk = rawResponse.ok === true;
        if (isOk) {
            updateTags(request.tags);
        }
        return isOk;
    },
    async listExpenses(): Promise<OutputExpense[]> {
        const rawResponse = await fetchWrapper("expense", "GET");
        if (!rawResponse.ok) {
            return [];
        }
        const notMapped = await rawResponse.json() as ListExpenseResponse;
        const mapped = notMapped.expenses.map(e => ({ ...e, date: new Date(e.date) }));
        return mapped;
    },
    async getExpenseDetails(expenseId: number): Promise<ExpenseDetails | null> {
        const rawResponse = await fetchWrapper(`expense/${expenseId}/details`, "GET");
        if (!rawResponse.ok) {
            return null;
        }
        return await rawResponse.json() as ExpenseDetails;
    },
    async unsplitExpense(expenseId: number): Promise<void> {
        await fetchWrapper(`expense/${expenseId}/unsplit`, "POST");
    },
    async export(): Promise<void> {
        const rawResponse = await fetchWrapper("expense/export", "GET");
        if (!rawResponse.ok) {
            alert("error during export");
        }
        const filename = getFilename(rawResponse, "export.json");
        const blob = await rawResponse.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.setAttribute("download", filename);
        a.click();
    },
    async importJson(file: File): Promise<void> {
        const data = wrapFile(file);
        await fetchWrapper("expense/import/json", "POST", {
            body: data
        });
        cachedTags = null;
    },
    async importPoste(file: File): Promise<number[]> {
        const data = wrapFile(file);
        const rawResponse = await fetchWrapper("expense/import/poste", "POST", {
            body: data
        });

        if (!rawResponse.ok) {
            alert("error during import");
            return [];
        }

        cachedTags = null;

        return (await rawResponse.json()).importedIds;
    },
    async import(file: File, importType: ImportType): Promise<number[]> {
        const data = wrapFile(file);
        const rawResponse = await fetchWrapper(`expense/import/${importType}`, "POST", {
            body: data
        });

        if (!rawResponse.ok) {
            alert("error during import");
            return [];
        }

        cachedTags = null;

        return (await rawResponse.json()).importedIds;
    },
    async clearAll(): Promise<void> {
        await fetchWrapper("expense", "DELETE");
        cachedTags = null;
    },
    async updateExpense(expense: OutputExpense): Promise<void> {
        await fetchWrapper(`expense/${expense.id}`, "PUT", {
            body: {
                ...expense,
                amount: Math.floor(expense.amount * 100)
            }
        });
        updateTags(expense.tags);
    },
    async delete(expense: OutputExpense | number): Promise<void> {
        const id = (typeof expense === "number" ? expense : expense.id);
        await fetchWrapper(`expense/${id}`, "DELETE");
    },
    async deleteLastBatch(): Promise<void> {
        await fetchWrapper("expense/import/last-batch", "DELETE");
    },
    async getMappings(): Promise<Mapping[]> {
        const response = await fetchWrapper("mapper", "GET");
        if (!response.ok) {
            return [];
        }
        const mappings = await response.json() as MapItemsResponse;
        return mappings.mapItems;
    },
    async setMapping(mapping: Mapping): Promise<void> {
        await fetchWrapper("mapper", "PUT", {
            body: {
                ...mapping,
                expressions: mapping.expressions.filter(e => e.expression !== "" && e.template !== "")
            }
        });
    },
    async getSources(): Promise<string[]> {
        const response = await fetchWrapper("mapper/sources", "GET");
        if (!response.ok) {
            return [];
        }
        return (await response.json()).sources;
    },
    async getDataQualityReport(): Promise<DataQualityReport | null> {
        const response = await fetchWrapper("dataquality", "GET");
        if (!response.ok) {
            return null;
        }
        return await response.json() as DataQualityReport;
    },
    async getInsights(): Promise<InsightsReport | null> {
        const response = await fetchWrapper("insights", "GET");
        if (!response.ok) {
            return null;
        }
        return await response.json() as InsightsReport;
    },
    async previewAmazonImport(file: File): Promise<AmazonImportPreview | null> {
        const response = await fetchWrapper("amazon/preview", "POST", {
            body: wrapFile(file)
        });
        if (!response.ok) {
            return null;
        }
        return await response.json() as AmazonImportPreview;
    },
    async importAmazonOrders(file: File): Promise<AmazonImportResult | null> {
        const response = await fetchWrapper("amazon/import", "POST", {
            body: wrapFile(file)
        });
        if (!response.ok) {
            return null;
        }
        return await response.json() as AmazonImportResult;
    },
    async rematchAmazonOrders(): Promise<AmazonRematchResult | null> {
        const response = await fetchWrapper("amazon/rematch", "POST");
        if (!response.ok) {
            return null;
        }
        return await response.json() as AmazonRematchResult;
    },
    async listAmazonOrders(filters: { from?: string; to?: string; query?: string; matchStatus?: string; limit?: number } = {}): Promise<AmazonOrderListItem[]> {
        const params: Params = {};
        if (filters.from) {
            params.from = filters.from;
        }
        if (filters.to) {
            params.to = filters.to;
        }
        if (filters.query) {
            params.query = filters.query;
        }
        if (filters.matchStatus) {
            params.matchStatus = filters.matchStatus;
        }
        if (filters.limit) {
            params.limit = String(filters.limit);
        }
        const response = await fetchWrapper("amazon/orders", "GET", {
            params
        });
        if (!response.ok) {
            return [];
        }
        const parsed = await response.json() as AmazonOrderListResponse;
        return parsed.orders;
    },
    async listApiKeys(): Promise<ApiKeySummary[]> {
        const response = await fetchWrapper("api-keys", "GET");
        if (!response.ok) {
            return [];
        }
        const parsed = await response.json() as ApiKeyListResponse;
        return parsed.keys;
    },
    async createApiKey(name: string): Promise<ApiKeySummary | null> {
        const response = await fetchWrapper("api-keys", "POST", {
            body: { name }
        });
        if (!response.ok) {
            return null;
        }
        return await response.json() as ApiKeySummary;
    },
    async revokeApiKey(id: number): Promise<void> {
        await fetchWrapper(`api-keys/${id}`, "DELETE");
    },
    async getPendingAction(token: string): Promise<PendingAction | null> {
        const response = await fetchWrapper(`pending-actions/${token}`, "GET");
        if (!response.ok) {
            return null;
        }
        return await response.json() as PendingAction;
    },
    async confirmPendingAction(token: string): Promise<PendingActionResult> {
        const response = await fetchWrapper(`pending-actions/${token}/confirm`, "POST");
        if (!response.ok) {
            try {
                return await response.json() as PendingActionResult;
            } catch {
                return { isOk: false, message: "Unable to confirm pending action." };
            }
        }
        return await response.json() as PendingActionResult;
    },
    async cancelPendingAction(token: string): Promise<PendingActionResult> {
        const response = await fetchWrapper(`pending-actions/${token}/cancel`, "POST");
        if (!response.ok) {
            try {
                return await response.json() as PendingActionResult;
            } catch {
                return { isOk: false, message: "Unable to cancel pending action." };
            }
        }
        return await response.json() as PendingActionResult;
    }
}

function wrapFile(file: File) {
    const data = new FormData();
    data.append('file', file);
    return data;
}

function getFilename(rawResponse: Response, defaultFilename: string) {
    const header = rawResponse.headers.get('Content-Disposition');
    const filename = header ? header.split(";")[1].split("=")[1] : defaultFilename;
    return filename;
}
